import { z } from 'zod';
import { OrgRole } from './org.js';
/**
 * An organization invite token. Stored at `orgInvites/{token}` where the doc
 * key IS the URL-safe token (128 bits of entropy, generated server-side).
 *
 * Client-side rules deny reads and writes — the server owns all mutations via
 * admin SDK. Consumers (`GET /api/invites/:token`) see only a sanitized
 * {@link InvitePreview} shape; full records are readable only by org admins
 * via `GET /api/orgs/:id/invites`.
 *
 * Email pinning is optional: if `email` is set, the accept handler requires
 * the authenticated user's email to match (case-insensitive). If absent, the
 * token is a bearer invite — whoever clicks it can claim it.
 */
export const OrgInvite = z.object({
    token: z.string().min(1),
    orgId: z.string().min(1),
    orgName: z.string().min(1),
    email: z.string().email().nullable().optional(),
    role: OrgRole,
    createdBy: z.string(),
    createdAt: z.string(),
    expiresAt: z.string(),
    consumedAt: z.string().nullable().optional(),
    consumedBy: z.string().nullable().optional(),
    revokedAt: z.string().nullable().optional(),
    revokedBy: z.string().nullable().optional(),
});
/**
 * Default invite TTL — 7 days. Admins can override per-invite via the
 * create-invite request. Past the expiry the accept handler 404s the token
 * but the record lingers in Firestore for the audit trail.
 */
export const DEFAULT_INVITE_TTL_DAYS = 7;
/** Request body for `POST /api/orgs/:id/invites` (orgAdmin-only). */
export const CreateInviteRequest = z.object({
    role: OrgRole.default('member'),
    email: z.string().email().optional(),
    ttlDays: z.number().int().min(1).max(30).optional(),
});
/** Response body for `POST /api/orgs/:id/invites`. */
export const CreateInviteResponse = z.object({
    token: z.string(),
    url: z.string().url(),
    expiresAt: z.string(),
});
/**
 * Sanitized invite view returned to an unauthenticated visitor hitting
 * `GET /api/invites/:token`. Strips `createdBy`, `consumedAt`, etc. — the
 * landing page only needs to render "You've been invited to Org X as Y".
 */
export const InvitePreview = z.object({
    orgName: z.string(),
    role: OrgRole,
    emailPinned: z.boolean(),
    /** ISO string; undefined if never expires (not currently possible). */
    expiresAt: z.string(),
});
/** Response body for `POST /api/invites/:token/accept`. */
export const AcceptInviteResponse = z.object({
    orgId: z.string(),
    orgName: z.string(),
    role: OrgRole,
});
